/**
 * ThemeKit PDP sync — lets our tk- product blocks follow Horizon's variant changes without
 * fetching anything themselves. Horizon's <variant-picker> dispatches `ProductSelectEvent`
 * (`@shopify/events`) and resolves its promise with the freshly rendered page (`detail.html`)
 * and the selected variant JSON (`detail.resource`). We read our own block from that HTML.
 *
 * Other tk- modules load this file with `import(el.dataset.tkSyncUrl)` (the versioned
 * `asset_url`), never a relative path, so there is exactly one cache-busted copy per page.
 */
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {{html: Document, variant: any, productId: string | undefined}} VariantChange
 * @param {HTMLElement} host - The listening component (should carry data-product-id).
 * @param {(change: VariantChange) => void} callback
 * @param {AbortSignal} signal
 */
export function onVariantChange(host, callback, signal) {
  const section = host.closest('.shopify-section');
  // Capture on document so components outside the product section (e.g. the sticky bar
  // section) still hear the event even if it does not bubble that far.
  const target = section?.querySelector('variant-picker') ? section : document;

  target.addEventListener(
    StandardEvents.productSelect,
    (event) => {
      const origin = event.target instanceof Element ? event.target : null;
      if (origin?.closest('product-card, quick-add-dialog, dialog')) return;

      /** @type {any} */ (event).promise
        ?.then((/** @type {any} */ result) => {
          const detail = result?.detail;
          if (!detail?.html) return;
          const productId = detail.newProduct?.id ?? detail.productId;
          if (host.dataset.productId && productId && String(productId) !== host.dataset.productId) return;
          callback({ html: detail.html, variant: detail.resource ?? null, productId });
        })
        .catch((/** @type {any} */ error) => {
          if (error?.name !== 'AbortError') console.warn('[tk-pdp-sync]', error);
        });
    },
    { signal, capture: target === document }
  );
}

/**
 * Copies every `[data-tk-sync="key"]` part of `host` from its twin (same id) in `html`.
 * Interactive state (checked radios, focus) outside those parts is left untouched.
 * @param {HTMLElement} host
 * @param {Document} html
 * @returns {HTMLElement | null} The twin element, if found.
 */
export function syncParts(host, html) {
  const twin = host.id ? /** @type {HTMLElement | null} */ (html.getElementById(host.id)) : null;
  if (!twin) return null;
  for (const part of host.querySelectorAll('[data-tk-sync]')) {
    if (!(part instanceof HTMLElement)) continue;
    const next = twin.querySelector(`[data-tk-sync="${part.dataset.tkSync}"]`);
    if (next && next.innerHTML !== part.innerHTML) part.innerHTML = next.innerHTML;
  }
  for (const [key, value] of Object.entries(twin.dataset)) {
    if (key.startsWith('variant')) host.dataset[key] = value ?? '';
  }
  return twin;
}

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Generic wrapper: <tk-variant-sync id="..." data-product-id="..."> refreshes its
 * [data-tk-sync] parts after every variant change (used by badges and simple blocks).
 */
class TkVariantSync extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    onVariantChange(this, ({ html }) => syncParts(this, html), this.#abort.signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('tk-variant-sync')) customElements.define('tk-variant-sync', TkVariantSync);
